/**
 * UNIVERSAL AUTH CHECK UTILITY
 * ============================================================================
 * 
 * PURPOSE: Universal authentication checking for all apps in the monorepo
 * 
 * This utility provides multiple ways to check authentication status:
 * 1. Cookie-based detection (Chrome-compatible)
 * 2. localStorage/sessionStorage caching
 * 3. REST API server-side verification
 * 4. Token-based detection (existing auth-detector)
 * 
 * FEATURES:
 * - Chrome redirect fix compatibility
 * - Fast synchronous checks
 * - Server-side verification fallback
 * - Caching to avoid redundant calls
 * - Works across all apps (consultanttickets, unifiedtime, etc.)
 * 
 * ============================================================================
 */

import React from 'react';
import { detectAuthToken, AuthTokenInfo } from './auth-detector';

/**
 * Check if user appears to be authenticated based on cookies
 * This is a fast, synchronous check that doesn't require Supabase calls
 */
export function hasAuthCookies(): boolean {
  if (typeof document === 'undefined') {
    return false; // Server-side, can't check cookies
  }

  const cookies = document.cookie;
  
  // Check for Supabase auth cookies
  const hasSupabaseSession = cookies.includes('sb-') && 
    (cookies.includes('access-token') || cookies.includes('refresh-token'));
  
  // Check for any Supabase-related cookies
  const hasSupabaseAuth = cookies.includes('supabase') || 
    cookies.includes('sb-');

  return hasSupabaseSession || hasSupabaseAuth;
}

/**
 * Get auth state from localStorage/sessionStorage
 * This provides a cached auth state to avoid redundant checks
 */
export function getCachedAuthState(): {
  isAuthenticated: boolean;
  userId?: string;
  lastCheck?: number;
} {
  if (typeof window === 'undefined') {
    return { isAuthenticated: false };
  }

  try {
    // Check sessionStorage first (more secure for auth state)
    const sessionAuth = sessionStorage.getItem('auth_state');
    if (sessionAuth) {
      const parsed = JSON.parse(sessionAuth);
      // Check if cached state is recent (within 5 minutes)
      if (parsed.lastCheck && (Date.now() - parsed.lastCheck) < 5 * 60 * 1000) {
        return parsed;
      }
    }

    // Fallback to localStorage
    const localAuth = localStorage.getItem('auth_state');
    if (localAuth) {
      const parsed = JSON.parse(localAuth);
      // Check if cached state is recent (within 5 minutes)
      if (parsed.lastCheck && (Date.now() - parsed.lastCheck) < 5 * 60 * 1000) {
        return parsed;
      }
    }
  } catch (error) {
    console.warn('Error reading cached auth state:', error);
  }

  return { isAuthenticated: false };
}

/**
 * Cache auth state for future checks
 */
export function setCachedAuthState(state: {
  isAuthenticated: boolean;
  userId?: string;
}): void {
  if (typeof window === 'undefined') {
    return;
  }

  const cacheData = {
    ...state,
    lastCheck: Date.now(),
  };

  try {
    // Store in sessionStorage (more secure)
    sessionStorage.setItem('auth_state', JSON.stringify(cacheData));
    
    // Also store in localStorage as fallback
    localStorage.setItem('auth_state', JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Error caching auth state:', error);
  }
}

/**
 * Clear cached auth state
 */
export function clearCachedAuthState(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem('auth_state');
    localStorage.removeItem('auth_state');
  } catch (error) {
    console.warn('Error clearing cached auth state:', error);
  }
}

/**
 * Check if user is likely authenticated without making Supabase calls
 * This combines multiple detection methods for immediate response
 */
export function isLikelyAuthenticated(): boolean {
  // First check cached state
  const cachedState = getCachedAuthState();
  if (cachedState.isAuthenticated) {
    return true;
  }

  // Check for auth cookies
  if (hasAuthCookies()) {
    return true;
  }

  // Check for auth tokens using existing detector
  const tokenInfo = detectAuthToken();
  if (tokenInfo.token && tokenInfo.user) {
    return true;
  }

  return false;
}

/**
 * Make a server-side auth check using fetch (REST API approach)
 * This is the more reliable method but requires an async call
 * 
 * @param baseUrl - The base URL for the app (e.g., '/api/auth/check')
 */
export async function checkAuthWithServer(baseUrl: string = '/api/auth/check'): Promise<{
  isAuthenticated: boolean;
  user?: any;
  error?: string;
}> {
  try {
    const response = await fetch(baseUrl, {
      method: 'GET',
      credentials: 'include', // Include cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      
      // Cache the result
      setCachedAuthState({
        isAuthenticated: !!data.user,
        userId: data.user?.id,
      });
      
      return {
        isAuthenticated: !!data.user,
        user: data.user,
      };
    } else {
      // Clear cache on auth failure
      clearCachedAuthState();
      
      return {
        isAuthenticated: false,
        error: `Auth check failed: ${response.status}`,
      };
    }
  } catch (error) {
    console.warn('Server auth check failed:', error);
    
    // Fallback to local checks
    return {
      isAuthenticated: isLikelyAuthenticated(),
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Universal auth check that tries multiple methods
 * This is the main function other apps should use
 */
export async function checkAuthentication(options: {
  useServerCheck?: boolean;
  serverEndpoint?: string;
  fallbackToLocal?: boolean;
} = {}): Promise<{
  isAuthenticated: boolean;
  user?: any;
  method: 'cached' | 'cookies' | 'tokens' | 'server' | 'none';
  error?: string;
}> {
  const {
    useServerCheck = true,
    serverEndpoint = '/api/auth/check',
    fallbackToLocal = true
  } = options;

  // 1. Check cached state first (fastest)
  const cachedState = getCachedAuthState();
  if (cachedState.isAuthenticated) {
    return {
      isAuthenticated: true,
      method: 'cached',
    };
  }

  // 2. Check cookies and tokens (fast)
  const localCheck = isLikelyAuthenticated();
  if (localCheck) {
    // Determine which method detected auth
    if (hasAuthCookies()) {
      return { isAuthenticated: true, method: 'cookies' };
    }
    
    const tokenInfo = detectAuthToken();
    if (tokenInfo.token) {
      return { 
        isAuthenticated: true, 
        method: 'tokens',
        user: tokenInfo.user 
      };
    }
  }

  // 3. Server check (most reliable but slower)
  if (useServerCheck) {
    try {
      const serverResult = await checkAuthWithServer(serverEndpoint);
      return {
        ...serverResult,
        method: 'server',
      };
    } catch (error) {
      if (!fallbackToLocal) {
        return {
          isAuthenticated: false,
          method: 'none',
          error: error instanceof Error ? error.message : 'Server check failed',
        };
      }
    }
  }

  // 4. Final fallback
  return {
    isAuthenticated: localCheck,
    method: localCheck ? 'cookies' : 'none',
  };
}

/**
 * Hook for React components to use universal auth checking
 * This provides a consistent interface across all apps
 */
export function useUniversalAuthCheck(options: {
  useServerCheck?: boolean;
  serverEndpoint?: string;
  fallbackToLocal?: boolean;
} = {}) {
  const [authState, setAuthState] = React.useState<{
    isAuthenticated: boolean;
    user?: any;
    method: string;
    isLoading: boolean;
    error?: string;
  }>({
    isAuthenticated: false,
    method: 'none',
    isLoading: true,
  });

  React.useEffect(() => {
    let mounted = true;

    const performCheck = async () => {
      try {
        const result = await checkAuthentication(options);
        
        if (mounted) {
          setAuthState({
            ...result,
            isLoading: false,
          });
        }
      } catch (error) {
        if (mounted) {
          setAuthState({
            isAuthenticated: false,
            method: 'none',
            isLoading: false,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        }
      }
    };

    performCheck();

    return () => {
      mounted = false;
    };
  }, [options.useServerCheck, options.serverEndpoint, options.fallbackToLocal]);

  return authState;
}

// Re-export the existing auth detector for backward compatibility
export { detectAuthToken, debugAuthSources } from './auth-detector';
export type { AuthTokenInfo } from './auth-detector';
